import React from 'react'
import bamLogo from 'assets/Images/bam_logo.svg'
import CustomModal from 'components/global/CustomModal'
import { EmailTemplate } from 'types/Classes/EmailTemplates'
import { capitalizeFirstLetter, convertToLocal } from 'helper/utility'
import Chip from 'components/global/Chip'
import { creationClassStore } from 'state/class/classCreationStore'

interface ClassPreviewProps {
  openModal: boolean
  closeModal: React.Dispatch<React.SetStateAction<boolean>>
  template: EmailTemplate
}

const ClassPreview: React.FC<ClassPreviewProps> = ({
  openModal,
  closeModal,
  template,
}) => {
  const classData = creationClassStore(store => store.classData)

  const getAgeTagText = (minAge?: number, maxAge?: number) => {
    if (minAge === 0 && maxAge === 0) return 'All Ages'
    if (minAge && !maxAge) return `Ages ${minAge}+`
    if (!minAge && maxAge) return `Ages up to ${maxAge}`
    if (minAge && maxAge) return `Ages ${minAge}-${maxAge}`
    return 'All Ages'
  }

  const getTicketNames = () => {
    const ticketNames: string[] = []
    
    if (template.recipientsToSend?.admissionTicketIds) {
      template.recipientsToSend.admissionTicketIds.forEach(id => {
        const ticket = classData.admissionTickets?.find(
          ticket => ticket._id === id,
        )
        if (ticket) {
          ticketNames.push(ticket.name)
        }
      })
    }

    if (template.recipientsToSend?.additionalOptionIds) {
      template.recipientsToSend.additionalOptionIds.forEach(id => {
        const option = classData.additionalOptions?.find(
          option => option._id === id,
        )
        if (option) {
          ticketNames.push(option.name)
        }
      })
    }

    if (template.recipientsToSend?.dropInTicketIds) {
      template.recipientsToSend.dropInTicketIds.forEach(id => {
        if (classData.dropInTicket && classData.dropInTicket._id === id) {
          ticketNames.push(classData.dropInTicket.name)
        }
      })
    }

    return ticketNames
  }

  const tickets = getTicketNames()

  return (
    <CustomModal
      openModal={openModal}
      closeModal={closeModal}
      width={600}
      title="Email Template Preview"
      footer={null}
    >
      <div className="p-20px">
        <div className="mb-20px">
          <h3 className="text-18 w-600 mb-10px">
            {capitalizeFirstLetter(template.templateName || '')}
          </h3>
          <div className="flex gap-10px align-center flex-wrap mb-15px">
            {template.recipientsToSend?.gender ? (
              <Chip
                text={capitalizeFirstLetter(template.recipientsToSend.gender)}
              />
            ) : null}
            <Chip
              text={getAgeTagText(
                template.recipientsToSend?.minAge,
                template.recipientsToSend?.maxAge,
              )}
            />
            {tickets.map((ticket, index) => (
              <Chip key={index} text={capitalizeFirstLetter(ticket)} />
            ))}
          </div>
        </div>

        <div className="border-top pt-20px">
          <h4 className="text-14 w-600 mb-10px">Subject:</h4>
          <p className="text-14 mb-20px">{template.subject || 'No subject'}</p>
          
          <h4 className="text-14 w-600 mb-10px">Body:</h4>
          <div 
            className="text-14 bg-gray-50 p-15px rounded-5px"
            dangerouslySetInnerHTML={{ __html: template.body }}
          />
        </div>

        <div className="flex justify-end mt-20px">
          <button
            className="primary-btn"
            onClick={() => closeModal(false)}
          >
            Close
          </button>
        </div>
      </div>
    </CustomModal>
  )
}

export default ClassPreview
